(async () => {
    const { Core } = await importModule('Core');

    const CN_VISIBLE = '@visible';
    const CN_BUMP = '!bump';
    const ANCHOR_NAV_GAP = 12; // px of breathing room below the anchor nav
    const BUMP_DURATION = 700; // ms, matches the animation length in the CSS

    customElements.define('sticky-basket-icon', class extends Core {
        elements = {
            bumpTarget: 'cart-drawer-trigger'
        }

        subscriptions = {
            'cart:update': '_bump'
        }

        render() {
            this._observeHeader();
            this._trackAnchorNav();
            this.subscribe('cart:update', { global: true });
        }

        _observeHeader() {
            // Literal class name is "#header" (this theme's own naming
            // convention), so a plain ".header" selector would never match -
            // getElementsByClassName takes the class token as-is, no escaping
            // needed.
            const $header = document.getElementsByClassName('#header')[0];

            if (!$header) {
                // No header to hand off from (shouldn't normally happen) -
                // fall back to always showing the icon.
                this.classList.add(CN_VISIBLE);
                return;
            }

            const observer = new IntersectionObserver((entries) => {
                const headerVisible = entries[0].isIntersecting;
                this.classList.toggle(CN_VISIBLE, !headerVisible);
            }, { threshold: 0 });

            observer.observe($header);
        }

        // The product anchor nav bar is itself sticky to the top of the
        // viewport (see product-anchor-nav.css), so a fixed icon sitting at
        // a static top offset ends up visually landing on top of it once
        // both are stuck. Instead of guessing a fixed offset, this reads the
        // nav bar's actual bottom edge - it can wrap to two rows on narrow
        // phones, so its height isn't constant - and keeps the icon just
        // below it. Pages without this nav bar keep the CSS default top
        // offset untouched.
        //
        // Desktop doesn't need any of this - the icon's default corner spot
        // never overlaps the nav bar there - so this used to recalculate on
        // every scroll/resize regardless of viewport width, which is what
        // made it visibly (and pointlessly) hop on desktop. It's now gated
        // to mobile/tablet widths, matching the breakpoint the rest of the
        // theme uses for that split.
        _trackAnchorNav() {
            this.$anchorNav = document.getElementsByClassName('#product-anchor-nav')[0];
            if (!this.$anchorNav) return;

            const MOBILE_BREAKPOINT = 991;

            const reposition = () => {
                if (window.innerWidth > MOBILE_BREAKPOINT) {
                    this.style.top = '';
                    return;
                }

                const rect = this.$anchorNav.getBoundingClientRect();
                if (rect.top <= 0) {
                    // The nav bar has reached the top and is now pinned there
                    // (position: sticky) - sit just below its actual edge.
                    this.style.top = `${rect.bottom + ANCHOR_NAV_GAP}px`;
                } else {
                    // The nav bar is still further down the page and hasn't
                    // stuck yet, so there's nothing to dodge - use the
                    // default corner offset from the CSS instead of chasing
                    // the nav bar's current (much lower) position.
                    this.style.top = '';
                }
            };
            const requestReposition = () => requestAnimationFrame(reposition);

            reposition();
            window.addEventListener('scroll', requestReposition, { passive: true });
            window.addEventListener('resize', requestReposition);
        }

        // A quick scale-and-wiggle on the icon itself whenever anything is
        // added to the cart, so the icon is a more obvious "yes, that
        // worked" than just quietly updating its count. Toggled on both
        // this outer element (swells the round background - see
        // sticky-basket-icon-swell in the CSS) and the inner
        // cart-drawer-trigger (scales and rotates the icon glyph itself -
        // see sticky-basket-icon-bump). Split into two separate keyframes
        // rather than one shared animation because the outer element's own
        // transform is already used for the show/hide slide-in, so its
        // version has to start and end at that same resting transform.
        _bump() {
            if (!this.$bumpTarget) return;
            this.classList.remove(CN_BUMP);
            this.$bumpTarget.classList.remove(CN_BUMP);
            // Force a reflow so adding the class back restarts the
            // animation even if two items are added in quick succession.
            void this.$bumpTarget.offsetWidth;
            this.classList.add(CN_BUMP);
            this.$bumpTarget.classList.add(CN_BUMP);
            clearTimeout(this._bumpTimeout);
            this._bumpTimeout = setTimeout(() => {
                this.classList.remove(CN_BUMP);
                this.$bumpTarget.classList.remove(CN_BUMP);
            }, BUMP_DURATION);
        }
    })
})();
